/**
 * Projects module. A construction project belongs to a Company and has a logo,
 * country, address, construction start/end dates and a description. The project
 * workspace exposes per-project submodules (Work log, Approvals, …).
 */
import type { UUID } from './common.js';
import type { Country } from './roles.js';

export type ProjectStatus = 'active' | 'inactive';

export const PROJECT_LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const PROJECT_LOGO_ACCEPT = 'image/*';

export interface ProjectLogoMeta {
  name: string;
  mime: string;
  size: number;
  uploadedAt: string;
}

export interface ProjectListRow {
  id: UUID;
  code: number;
  name: string;
  country: Country;
  isActive: boolean;
  hasLogo: boolean;
  companyId: UUID | null;
  companyName: string | null;
  description: string | null;
  address: string | null;
  /** ISO date (YYYY-MM-DD) or null. */
  startDate: string | null;
  endDate: string | null;
}

export interface ProjectDetail {
  id: UUID;
  code: number;
  name: string;
  country: Country;
  isActive: boolean;
  companyId: UUID | null;
  address: string | null;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
  /** ISO timestamp the project was created (shown as "Created: …" on edit). */
  createdAt: string;
  logo: ProjectLogoMeta | null;
}

export interface ProjectLogoUpload {
  name: string;
  mime: string;
  dataBase64: string;
}

export interface CreateProjectInput {
  name: string;
  country: Country;
  isActive?: boolean;
  companyId?: UUID | null;
  address?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  description?: string | null;
  logo?: ProjectLogoUpload | null;
}

export interface UpdateProjectInput {
  name?: string;
  country?: Country;
  isActive?: boolean;
  companyId?: UUID | null;
  address?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  description?: string | null;
  logo?: ProjectLogoUpload | null;
}

export interface ProjectCatalogQuery {
  status?: ProjectStatus;
  country?: Country;
  search?: string;
}

export interface ProjectListResponse {
  items: ProjectListRow[];
  total: number;
}

/** Lightweight project row for pickers (Users → Assign to project). */
export interface ProjectPickRow {
  id: UUID;
  name: string;
  companyId: UUID | null;
  country: Country;
}

/**
 * The project workspace submodules, in display order. `slug` is the route
 * segment under `/projects/:id/`; `i18n` is the label key. Content for each is
 * built separately — they open once a project is selected.
 */
export interface ProjectSubmodule {
  slug: string;
  i18n: string;
}

export const PROJECT_SUBMODULES: readonly ProjectSubmodule[] = [
  { slug: 'work-log', i18n: 'workLog' },
  { slug: 'approvals', i18n: 'approvals' },
  { slug: 'work-releases', i18n: 'workReleases' },
  { slug: 'progress', i18n: 'progress' },
  { slug: 'elements', i18n: 'elements' },
  { slug: 'customer', i18n: 'customer2' },
  { slug: 'inspections-system', i18n: 'inspectionsSystem' },
  { slug: 'plans', i18n: 'plans' },
  { slug: 'reports', i18n: 'reports' },
  { slug: 'lab-testing', i18n: 'labTesting' },
  { slug: 'structures', i18n: 'structures' },
  { slug: 'info-center', i18n: 'infoCenter' },
  { slug: 'crafts', i18n: 'crafts' },
  { slug: 'professions', i18n: 'professions' },
  { slug: 'stages', i18n: 'stages' },
] as const;
